import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface DriverProfile {
  id: number;
  name: string;
  phone: string;
  email: string;
  pix_key: string;
  uber_driver_id?: string;
  joined_at: string;
  status: 'active' | 'inactive';
  verification_status: 'pending' | 'verified' | 'rejected';
  total_vendors: number;
  total_earnings: number;
}

export const MobileProfilePage: React.FC = () => {
  const { t } = useLanguage();
  const [profile, setProfile] = useState<DriverProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<DriverProfile>>({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // Mock data for now - in real implementation, this would fetch from API
      const mockProfile: DriverProfile = {
        id: 1,
        name: "João Silva",
        phone: "+55 11 99999-9999",
        email: "joao.silva@email.com",
        pix_key: "+55 11 99999-9999",
        uber_driver_id: "UBR123456",
        joined_at: "2024-01-15T10:00:00Z",
        status: "active",
        verification_status: "verified",
        total_vendors: 3,
        total_earnings: 1250.50
      };
      setProfile(mockProfile);
      setFormData(mockProfile);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // In real implementation, this would send to API
      console.log('Saving profile:', formData);
      setProfile({ ...profile!, ...formData });
      setEditing(false);
      alert(t('profile.success'));
    } catch (error) {
      alert(t('profile.error.save'));
    }
  };

  const handleInputChange = (field: keyof DriverProfile, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(dateString));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="h-full flex items-center justify-center bg-white">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">👤</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('profile.error')}</h3>
          <p className="text-gray-600 mb-4">{t('profile.error.desc')}</p>
          <button
            onClick={fetchProfile}
            className="bg-green-600 text-white px-6 py-3 rounded-xl font-medium"
          >
            {t('profile.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('profile.title')}</h1>
            <p className="text-gray-600 text-sm">{t('profile.subtitle')}</p>
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {t('profile.edit')}
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Profile Header */}
        <div className="bg-white p-6 border-b">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {profile.name.charAt(0)}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{profile.name}</h2>
              <p className="text-gray-600">{profile.phone}</p>
              <div className="flex items-center space-x-2 mt-1">
                <div className={`w-2 h-2 rounded-full ${
                  profile.verification_status === 'verified' ? 'bg-green-500' : 
                  profile.verification_status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                <span className={`text-xs font-medium ${
                  profile.verification_status === 'verified' ? 'text-green-600' : 
                  profile.verification_status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {profile.verification_status === 'verified' ? t('profile.verified') : 
                   profile.verification_status === 'pending' ? t('profile.pending') : t('profile.rejected')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white p-4 border-b">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{profile.total_vendors}</p>
              <p className="text-sm text-gray-600">{t('profile.active.partners')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{formatCurrency(profile.total_earnings)}</p>
              <p className="text-sm text-gray-600">{t('profile.total.earned')}</p>
            </div>
          </div>
        </div>

        {editing ? (
          /* Edit Form */
          <div className="p-4 space-y-4">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">{t('profile.personal.info')}</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.full.name')}</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.phone')}</label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.email')}</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.pix.key')}</label>
                  <input
                    type="text"
                    value={formData.pix_key || ''}
                    onChange={(e) => handleInputChange('pix_key', e.target.value)}
                    placeholder="CPF, telefone ou email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('profile.pix.desc')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.uber.id')}</label>
                  <input
                    type="text"
                    value={formData.uber_driver_id || ''}
                    onChange={(e) => handleInputChange('uber_driver_id', e.target.value)}
                    placeholder="UBR123456"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleSave}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-bold"
              >
                {t('profile.save')}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setFormData(profile);
                }}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium"
              >
                {t('profile.cancel')}
              </button>
            </div>
          </div>
        ) : (
          /* Profile View */
          <div className="p-4 space-y-4">
            {/* Personal Info */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">{t('profile.personal.info')}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.full.name')}</span>
                  <span className="font-medium text-gray-900">{profile.name}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.phone')}</span>
                  <span className="font-medium text-gray-900">{profile.phone}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.email')}</span>
                  <span className="font-medium text-gray-900">{profile.email}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.pix.key')}</span>
                  <span className="font-medium text-gray-900">{profile.pix_key}</span>
                </div>
                {profile.uber_driver_id && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">{t('profile.uber.id')}</span>
                    <span className="font-medium text-gray-900">{profile.uber_driver_id}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.member.since')}</span>
                  <span className="font-medium text-gray-900">{formatDate(profile.joined_at)}</span>
                </div>
              </div>
            </div>

            {/* Account Status */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">{t('profile.account.status')}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.status')}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    profile.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {profile.status === 'active' ? t('profile.active') : t('profile.inactive')}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">{t('profile.verification')}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    profile.verification_status === 'verified' ? 'bg-green-100 text-green-800' : 
                    profile.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {profile.verification_status === 'verified' ? t('profile.verified') : 
                     profile.verification_status === 'pending' ? t('profile.pending') : t('profile.rejected')}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              <button className="w-full bg-blue-50 border border-blue-200 text-blue-700 py-3 rounded-xl font-medium">
                {t('profile.support')}
              </button>
              <button className="w-full bg-yellow-50 border border-yellow-200 text-yellow-700 py-3 rounded-xl font-medium">
                {t('profile.terms')}
              </button>
              <button className="w-full bg-red-50 border border-red-200 text-red-700 py-3 rounded-xl font-medium">
                {t('profile.logout')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};